import { InspectionOnView } from 'presentation/inspection/inspectionOnView';
import {
  GeneralNormConfig,
  normalizeGeneralNormConfig,
} from 'shared/domain/template/generalNormConfig';
import { LabelledEntity } from 'shared/types/commonView';
import { InspectionPageEstimateInput } from '../buildInspectionPageEstimateInput';
import { sectionScore } from '../inspectionComplianceClassifier';
import { pdfFieldTable, PdfField, PdfFieldTableEntry } from './inspectionFieldTable';
import {
  compactAttachmentsBlock,
  documentOnViewsToPdfAttachments,
} from './inspectionPdfAttachments';
import { INSPECTION_PDF_LABELS, INSPECTION_PDF_SAMPLE_VALUES } from './inspectionPdfLabels';
import { nestedHeading, sectionHeading } from './inspectionPdfTypography';
import { pdfColumn, PdfContent, pdfText } from './pdf/pdfBlocks';
import { PDF_CONTENT_WIDTH } from './pdf/pdfPage';
import { regularFont } from './pdf/pdfTextMetrics';

/** Inspection data share their rows two pairs at a time, with a caption slice wide enough for long captions. */
const DATA_TABLE_PAIRS_PER_ROW = 2;
const DATA_TABLE_CAPTION_SHARE = 0.52;
const REPORT_TITLE_FONT = regularFont(16);
const REPORT_TITLE_MARGIN = { top: 16, bottom: 8 };
/** The general standard table spans the page: one cell padding on each side of its attachments. */
const FULL_WIDTH_CELL_GEOMETRY = { widthRatio: 1, horizontalPadding: 8 };

type OptionalField = PdfField | undefined;

const field = (label: string, value: string, extra: Partial<PdfField> = {}): OptionalField =>
  value.trim().length > 0 ? { label, value, ...extra } : undefined;

const joinedLabels = (entities: LabelledEntity[]): string =>
  entities.map((entity) => entity.label).join(', ');

const presentFields = (fields: OptionalField[]): PdfField[] =>
  fields.filter((entry): entry is PdfField => entry !== undefined);

function dataTable(entries: PdfFieldTableEntry[]) {
  return pdfFieldTable({
    entries,
    pairsPerRow: DATA_TABLE_PAIRS_PER_ROW,
    captionShare: DATA_TABLE_CAPTION_SHARE,
  });
}

/**
 * `PdfInspectionDataSectionFactory`: the report title, who generated it and when, then the inspection data
 * as a two-pairs-per-row table. Fields the template hides or that have no value are left out.
 */
class InspectionDataSectionBuilder {
  private readonly inspection: InspectionOnView;
  private readonly normConfig: GeneralNormConfig;

  constructor(private readonly input: InspectionPageEstimateInput) {
    this.inspection = input.source.inspection;
    this.normConfig = normalizeGeneralNormConfig(this.inspection.template.generalNormConfig);
  }

  build(sectionNumber?: number): PdfContent {
    return pdfColumn([
      pdfText(this.input.reportName || INSPECTION_PDF_LABELS.reportTitle, PDF_CONTENT_WIDTH, {
        font: REPORT_TITLE_FONT,
        margin: REPORT_TITLE_MARGIN,
      }),
      dataTable(this.reportFields()),
      sectionHeading(INSPECTION_PDF_LABELS.inspectionDataHeading, sectionNumber),
      dataTable(this.inspectionFields()),
    ]);
  }

  private reportFields(): PdfField[] {
    const { reportMessage, reportMessageInPdf } = this.input;
    return presentFields([
      reportMessageInPdf ? field(INSPECTION_PDF_LABELS.reportMessage, reportMessage) : undefined,
      field(INSPECTION_PDF_LABELS.reportCreatedBy, INSPECTION_PDF_SAMPLE_VALUES.userName),
      field(INSPECTION_PDF_LABELS.creationDate, INSPECTION_PDF_SAMPLE_VALUES.date),
    ]);
  }

  private inspectionFields(): PdfField[] {
    const { inspection, normConfig } = this;
    return presentFields([
      field(INSPECTION_PDF_LABELS.inspectionName, inspection.template.summary ?? '', { bold: true }),
      field(
        INSPECTION_PDF_LABELS.inspectionNumber,
        inspection.code || INSPECTION_PDF_SAMPLE_VALUES.inspectionNumber
      ),
      field(INSPECTION_PDF_LABELS.executionDate, INSPECTION_PDF_SAMPLE_VALUES.date),
      field(INSPECTION_PDF_LABELS.status, INSPECTION_PDF_LABELS.completedStatus),
      field(INSPECTION_PDF_LABELS.site, joinedLabels(inspection.sites)),
      field(INSPECTION_PDF_LABELS.level, joinedLabels(inspection.levels)),
      normConfig.isControlledPartyVisible
        ? field(INSPECTION_PDF_LABELS.controlledParty, joinedLabels(inspection.controlledParty))
        : undefined,
      normConfig.isContractNumberVisible
        ? field(INSPECTION_PDF_LABELS.contractNumber, joinedLabels(inspection.contracts))
        : undefined,
      normConfig.isWorkTypeVisible
        ? field(INSPECTION_PDF_LABELS.workType, joinedLabels(inspection.workTypes))
        : undefined,
      field(INSPECTION_PDF_LABELS.inspectedBy, inspection.author ?? ''),
      normConfig.isCommentVisible
        ? field(INSPECTION_PDF_LABELS.comment, inspection.comment ?? '', { richText: true })
        : undefined,
      normConfig.isInspectionResultVisible && this.hasScore()
        ? field(INSPECTION_PDF_LABELS.score, INSPECTION_PDF_SAMPLE_VALUES.scorePercent)
        : undefined,
    ]);
  }

  private hasScore(): boolean {
    const { passed, notPassed } = sectionScore(this.inspection.protocol);
    return passed + notPassed > 0;
  }
}

export function inspectionDataSection(
  input: InspectionPageEstimateInput,
  sectionNumber?: number
): PdfContent {
  return new InspectionDataSectionBuilder(input).build(sectionNumber);
}

/** `sectionGeneralStandards`: a sub-section of the inspection data with the template's general standard. */
export function generalStandardSection(input: InspectionPageEstimateInput): PdfContent {
  const { generalStandard, generalStandardAttachments } = input.templateData;
  const description = input.source.inspection.template.generalNorm?.description?.trim() ?? '';
  const attachments = documentOnViewsToPdfAttachments(
    input.source.inspectionTemplateDocuments.templateDocuments
  );
  if (!generalStandard || (description.length === 0 && attachments.length === 0)) {
    return undefined;
  }
  const entries: PdfFieldTableEntry[] = [
    ...presentFields([
      field(INSPECTION_PDF_LABELS.standardDescription, description, { richText: true }),
    ]),
    {
      fullRow: (contentWidth) =>
        compactAttachmentsBlock(
          {
            attachments,
            config: generalStandardAttachments,
            fileMetadata: input.fileMetadata,
            label: INSPECTION_PDF_LABELS.generalStandardAttachments,
            geometry: FULL_WIDTH_CELL_GEOMETRY,
          },
          contentWidth
        ),
    },
  ];
  return pdfColumn([
    nestedHeading(INSPECTION_PDF_LABELS.generalStandardHeading),
    pdfFieldTable({ entries, pairsPerRow: 1 }),
  ]);
}
